import { FC, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import {
  Text,
  IconExclamationTriangle,
  IconEdit,
  BorderRadius6,
  IconDragHandle,
  GreyGrey20,
  GreyGrey60,
  GreyGrey80,
  GreyWhite,
  Primary13Regular,
  RedRed60,
  TextFieldInput,
  type HandleProps,
  Flex
} from '@sequencehq/core-components'
import { useNode } from 'Automations/nodeDefinitions/hooks'
import { getValidationErrorsByPath } from 'Automations/nodeDefinitions/validation/helpers/getValidationErrorsByPath/getValidationErrorsByPath'
import { selectedGroupIdAtom } from '../../switch.atoms'
import { getDefaultBranchLabel } from '../../switch.constants'
import { useGroupEditing } from '../../hooks'

export const GroupMenuItem: FC<{
  id: string
  label: string
  index: number
  nodeId: string
  handleProps?: HandleProps
}> = ({ id, label, index, nodeId, handleProps }) => {
  const [selectedGroupId, setSelectedGroupId] = useAtom(selectedGroupIdAtom)
  const { validationErrors } = useNode({ nodeId })
  const { updateGroupLabel } = useGroupEditing({ nodeId })

  const [isEditing, setIsEditing] = useState(false)
  const [editingName, setEditingName] = useState('')

  const hasErrors = useMemo(() => {
    return (
      Object.keys(getValidationErrorsByPath(validationErrors, `groups.${id}`))
        .length > 0
    )
  }, [validationErrors, id])

  const handleStartEdit = useCallback(() => {
    setSelectedGroupId(id)
    setEditingName(label)
    setIsEditing(true)
  }, [id, label, setSelectedGroupId])

  const handleSave = useCallback(() => {
    updateGroupLabel(editingName)
    setIsEditing(false)
  }, [editingName, updateGroupLabel])

  const handleCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  return (
    <Flex
      data-testid={`automations.editor.configForm.switch.groupMenu.item.${id}`}
      padding={isEditing ? '3px' : '8px'}
      background={selectedGroupId === id ? GreyGrey20 : GreyWhite}
      _hover={{ background: GreyGrey20 }}
      transition="background 250ms"
      cursor="pointer"
      borderRadius={BorderRadius6}
      onClick={() => setSelectedGroupId(id)}
      alignItems="center"
      justifyContent="space-between"
      gap="4px"
      role="group"
    >
      <Flex alignItems="center" gap="4px" minWidth={0} flex={1}>
        {handleProps && !isEditing && (
          <Flex
            cursor="grab"
            flexShrink={0}
            alignItems="center"
            {...handleProps.attributes}
            {...handleProps.listeners}
          >
            <IconDragHandle color={GreyGrey60} />
          </Flex>
        )}
        {isEditing ? (
          <TextFieldInput
            data-testid={`automations.editor.configForm.switch.groupMenu.item.${id}.labelInput`}
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            autoFocus
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            maxLength={64}
            height="26px"
            placeholder={getDefaultBranchLabel(index)}
          />
        ) : (
          <Text
            data-testid={`automations.editor.configForm.switch.groupMenu.item.${id}.label`}
            {...Primary13Regular}
            color={GreyGrey80}
            noOfLines={1}
          >
            {label || getDefaultBranchLabel(index)}
          </Text>
        )}
      </Flex>
      {!isEditing && (
        <Flex alignItems="center" gap="4px" flexShrink={0}>
          <Flex
            data-testid={`automations.editor.configForm.switch.groupMenu.item.${id}.edit`}
            display="none"
            _groupHover={{ display: 'flex' }}
            alignItems="center"
            cursor="pointer"
            onClick={e => {
              e.stopPropagation()
              handleStartEdit()
            }}
          >
            <IconEdit color={GreyGrey60} />
          </Flex>
          {hasErrors && (
            <IconExclamationTriangle
              data-testid={`automations.editor.configForm.switch.groupMenu.item.${id}.errorIcon`}
              color={RedRed60}
              style={{ flexShrink: 0 }}
            />
          )}
        </Flex>
      )}
    </Flex>
  )
}
